import { useQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import AppContainer from "./components/navigation/AppContainer/AppContainer";
import { AUTH_TOKEN } from "./constants/authToken";
import AssetsPage from "./pages/AssetsPage/AssetsPage";
import AuthPage from "./pages/AuthPage/AuthPage";
import BlogPage from "./pages/BlogPage/BlogPage";
import SymbolPage from "./pages/SymbolPage/SymbolPage";
import TradesPage from "./pages/TradesPage/TradesPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import VizPage from "./pages/VizPage/VizPage";
import { CURRENT_USER } from "./queries/auth";
import { ADD_LOG } from "./queries/user-analytics"; // added for tracking data

import FearGreedData from "./pages/VizPage/DataAPI/FearGreedData";
import FearGreedImage from "./pages/VizPage/DataAPI/FearGreedImage";
import GlobalCapData from "./pages/VizPage/DataAPI/TotalCap";
import DeFi from "./pages/VizPage/DataAPI/DeFi";
import TrendingCoinData from "./pages/VizPage/DataAPI/TrendingNow";
import Top10CoinData from "./pages/VizPage/DataAPI/TopCoins";
import ContestPage from "./pages/ContestPage/ContestPage";
import LeaderboardPage from "./pages/LeaderboardPage/LeaderboardPage";
import ContestSymbolPage from "./pages/ContestSymbolPage/ContestSymbolPage";
import JourneyPage from "./pages/JourneyPage/JourneyPage";
import RequireAuth from "./pages/RequireAuth/RequireAuth";
import TutorialPage from "./pages/TutorialPage/TutorialPage";
import LoadingComponent from "./components/loading/LoadingComponent";
import {initializeGA, logPageView} from "./helpers/useGA"
import Announcement from "./components/Announcement/Announcement";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm/ForgotPasswordForm";
import ResetPasswordForm from "./components/auth/ResetPasswordForm/ResetPasswordForm";
import RequireAdmin from "./admin/RequireAdmin";
import AdminShell from "./admin/AdminShell";
import WaveListPage from "./admin/WaveListPage";
import WaveDetailPage from "./admin/WaveDetailPage";
import GrantAdminPanel from "./admin/GrantAdminPanel";

export default function App() {
  const { loading, error, data } = useQuery(CURRENT_USER);
  const [user, setUser] = useState(undefined);
  const location = useLocation();
 // added for tracking user data
 const [logEventMutation] = useMutation(ADD_LOG, {
  onCompleted: ({ addLog }) => {
      // Access the HTTP response directly
      const { status, message } = addLog;
      if (status === "success") {
          console.log("Event logged successfully:", message);
      } else {
          console.error("Failed to log event:", message);
      }
  },
  onError: (error) => {
      console.error("Error logging event:", error.message);
  }
});

const logEvent = (eventType, additionalData, _userid) => {
  const eventData = {
      userid: Number(_userid),
      event_type: eventType,
      timestamp: new Date().toISOString(),
      additional_data: JSON.stringify(additionalData)

  };

  logEventMutation({ variables: eventData });
};

  console.log(loading);

  const logout = () => {
    logEvent('JourneyPage', { reason: 'User initiated logout' }, data.currentUser.userid); // added for tracking user data
    setUser(undefined)
    localStorage.removeItem(AUTH_TOKEN)
    window.location.replace("/")
  }

  const props = {
    user: user,
    logout: logout
  }

  useEffect(() => {
    if (loading) console.log('Loading user...');
    else if (error) { 
      setUser(null);
    }
    else {
      setUser(data.currentUser);
    }
  }, [loading, error, data])

  useEffect(() => {
    if (data && data.currentUser && data.currentUser.userid) {
      initializeGA(data.currentUser.userid);
    } 
    else {
      initializeGA();
    }
  }, [data]);

  useEffect(() => {
    const title = document.title; // Get the current document title
    const fullUrl = window.location.href; // Gets the full URL including port
    logPageView(fullUrl, title);
  }, [location]);

  

  // Render a loading indicator until data is fetched
  if (loading) {
    return (
      <div>
        <LoadingComponent />
      </div>
    );
  }

  // useGA(); // For tracking user behaviour through GA
  // Use useEffect to track page views
  return (
    <AppContainer {...props}>
      <Announcement />
      <Routes>
        {/* Auth routes — accessible to logged-out users.
            Kept inside <AppContainer> so header/nav stays mounted and
            users can navigate back to "/" from the login screen. */}
        <Route path="/login" element={<AuthPage initLoginMode={true} />} />
        <Route path="/register" element={<AuthPage initLoginMode={false} />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/reset-password/:resetToken" element={<ResetPasswordForm />} />

        {/* Routes that are accessible without authentication */}
        <Route index path="/" element={<JourneyPage {...props} />} />
        <Route path="/journey" element={<JourneyPage {...props} />} />
        <Route path="/tutorial" element={<TutorialPage />} />

        {/* Routes that require authentication */}
        <Route element={<RequireAuth />}>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/trade" element={<TradesPage />} />
          <Route path="/trade/:code" element={<SymbolPage {...props} />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/blog" element={<BlogPage {...props} />} />
          <Route path="/viz" element={<VizPage {...props} />} >
            <Route path="/viz/fear_greed_data" element={<FearGreedData {...props} />} />
            <Route path="/viz/fear_greed_image" element={<FearGreedImage {...props} />} />
            <Route path="/viz/total_cap" element={<GlobalCapData {...props} />} />
            <Route path="/viz/defi" element={<DeFi {...props} />} />
            <Route path="/viz/top_coins" element={<Top10CoinData {...props} />} />
            <Route path="/viz/trending_coins" element={<TrendingCoinData {...props} />} />
          </Route>
          <Route path="/contest" >
            <Route path="/contest" element={<ContestPage />} />
            <Route path="/contest/:name" element={<LeaderboardPage />} />
            <Route path="/contest/:name/trade/:code" element={<ContestSymbolPage {...props} />} />
          </Route>
        </Route>

        {/* Admin (research platform) */}
        <Route path="/admin" element={<RequireAdmin />}>
          <Route element={<AdminShell />}>
            <Route index element={<WaveListPage />} />
            <Route path="waves" element={<WaveListPage />} />
            <Route path="waves/:waveId" element={<WaveDetailPage />} />
            <Route path="access" element={<GrantAdminPanel />} />
          </Route>
        </Route>
      </Routes>

    </AppContainer >
  );
}
